import { useEffect, useState } from "react";
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import moment from "moment";
import contractsService from "api/contractsService";
import {
  CRU_HISTORY_ACTION_LABELS,
  IContractCruHistory,
} from "dto/Contracts/IContractCru";
import styles from "./styles.module.scss";

// Drobne dialogi CRU rejestru umów (port okien WithdrawFromCru / ShowCruHistory
// z Kwantum.Documents.Register.UI): wycofanie z komentarzem + historia operacji.

export const WithdrawFromCruDialog = ({
  contractLabel,
  loading,
  onConfirm,
  onClose,
}: {
  contractLabel: string;
  loading?: boolean;
  onConfirm: (comment: string) => void;
  onClose: () => void;
}) => {
  const [comment, setComment] = useState("");
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Wycofanie umowy z publikacji w CRU</DialogTitle>
      <DialogContent>
        <p style={{ marginTop: 0 }}>
          Umowa <b>{contractLabel}</b> zostanie wycofana z publikacji w Centralnym
          Rejestrze Umów. CRU wymaga podania uzasadnienia.
        </p>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="Uzasadnienie wycofania"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} disabled={loading}>
          Anuluj
        </MuiButton>
        <MuiButton
          variant="contained"
          color="error"
          disabled={!comment.trim() || loading}
          onClick={() => onConfirm(comment.trim())}
        >
          Wycofaj
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

export const ConfirmCruDialog = ({
  title,
  text,
  confirmLabel,
  loading,
  onConfirm,
  onClose,
}: {
  title: string;
  text: React.ReactNode;
  confirmLabel: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>{text}</DialogContent>
    <DialogActions>
      <MuiButton onClick={onClose} disabled={loading}>
        Anuluj
      </MuiButton>
      <MuiButton variant="contained" disabled={loading} onClick={onConfirm}>
        {confirmLabel}
      </MuiButton>
    </DialogActions>
  </Dialog>
);

export const CruHistoryDialog = ({
  contractId,
  contractLabel,
  onClose,
}: {
  contractId: string;
  contractLabel: string;
  onClose: () => void;
}) => {
  const [items, setItems] = useState<IContractCruHistory[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    contractsService
      .getCruHistory(contractId)
      .then((list) => !cancelled && setItems(list))
      .catch(() => !cancelled && setError("Nie udało się pobrać historii."));
    return () => {
      cancelled = true;
    };
  }, [contractId]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Historia CRU — {contractLabel}</DialogTitle>
      <DialogContent>
        {error && <div className={styles.errors}>{error}</div>}
        {!error && items === null && <div className={styles.hint}>Ładowanie…</div>}
        {items && items.length === 0 && (
          <div className={styles.hint}>Brak operacji CRU dla tej umowy.</div>
        )}
        {items && items.length > 0 && (
          <div className={styles.history}>
            {items.map((h) => (
              <div key={h.Id} className={styles.historyRow}>
                <div
                  className={`${styles.historyHead}${
                    h.Action === "Failed" ? ` ${styles.historyFailed}` : ""
                  }`}
                >
                  <span>{CRU_HISTORY_ACTION_LABELS[h.Action] ?? h.Action}</span>
                  <span>{moment(h.Timestamp).format("DD.MM.YYYY HH:mm")}</span>
                </div>
                <div className={styles.historyMeta}>
                  {h.UserName ?? ""}
                  {h.CruSystemNumber ? ` · nr systemowy ${h.CruSystemNumber}` : ""}
                </div>
                {h.Message && <div>{h.Message}</div>}
                {h.Errors && Object.keys(h.Errors).length > 0 && (
                  <ul className={styles.historyErrors}>
                    {Object.entries(h.Errors).map(([k, v]) => (
                      <li key={k}>
                        <b>{k}</b>: {v}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Zamknij</MuiButton>
      </DialogActions>
    </Dialog>
  );
};
